import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, categories } from '@/mocks/comics';
import { reviews } from '@/mocks/reviews';

const categorySlugMap: Record<string, string> = {
  '恋爱': 'lianai',
  '奇幻': 'qihuan',
  '动作': 'dongzuo',
  '悬疑': 'xuanyi',
  '日常': 'richang',
  '搞笑': 'gaoxiao',
};

export default function ArticlePage() {
  const { id: slug } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.slug === slug) || comics[0];

  const pageUrl = `http://heniukeji.com/comic/${comic.slug}`;
  const catSlug = categorySlugMap[comic.category] || 'lianai';
  const catUrl = `http://heniukeji.com/category/${catSlug}`;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: `${comic.category}漫画`, href: `/category/${catSlug}` },
    { label: comic.title, href: undefined },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://heniukeji.com/' },
      { '@type': 'ListItem', position: 2, name: `${comic.category}漫画`, item: catUrl },
      { '@type': 'ListItem', position: 3, name: comic.title, item: pageUrl },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 哈哈漫画网`,
    description: comic.description,
    image: comic.coverImage,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-01',
    dateModified: comic.updatedAt,
    publisher: {
      '@type': 'Organization',
      name: '哈哈漫画网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
  };

  const relatedComics = comics.filter((c) => c.category === comic.category && c.id !== comic.id).slice(0, 3);

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} - 高清韩漫下拉式阅读 - 哈哈漫画网`}
        description={comic.description}
        keywords={`${comic.title}, ${comic.category}漫画, 韩国漫画, 免费在线观看, 高清韩漫, 下拉式阅读, 哈哈漫画网`}
        canonicalUrl={pageUrl}
        ogTitle={`${comic.title} - 哈哈漫画网`}
        ogDescription={comic.description}
        ogImage={comic.coverImage}
        ogType="article"
        schemaJson={[articleSchema, breadcrumbSchema]}
      />

      <section className="relative h-[420px] md:h-[560px] w-full overflow-hidden">
        <img
          src={comic.coverImage}
          alt={`哈哈漫画网 - ${comic.title}`}
          title={`哈哈漫画网 - ${comic.title}`}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-black/60"></div>
        <Navbar />
        <div className="relative z-10 w-full h-full flex items-end pb-10 md:pb-14 px-4 md:px-6">
          <div className="w-full max-w-3xl">
            <div className="mb-4">
              <Breadcrumb items={breadcrumbItems} />
            </div>
            <span className={`inline-block px-4 py-1.5 rounded-full text-xs font-medium mb-3 whitespace-nowrap ${
              comic.status === '连载中'
                ? 'border border-accent-400/70 text-accent-200'
                : 'border border-background-50/40 text-background-50/80'
            }`}>
              {comic.status}
            </span>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50">
              {comic.title}
            </h1>
            <div className="flex items-center gap-3 mt-2 text-sm text-background-50/80 flex-wrap">
              <span>{comic.author} · 著</span>
              <span className="w-1 h-1 rounded-full bg-background-50/40"></span>
              <span>{comic.category}</span>
              <span className="w-1 h-1 rounded-full bg-background-50/40"></span>
              <span>共 {comic.chapters} 话</span>
              <span className="w-1 h-1 rounded-full bg-background-50/40"></span>
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-yellow-400"></i>
                {comic.rating}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-foreground-400 uppercase tracking-wider">作品简介</span>
              </div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
                关于 <span className="italic text-primary-500 font-serif">{comic.title}</span>
              </h2>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                {comic.description}
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
                {[
                  { icon: 'ri-eye-line', label: '总阅读', value: comic.views >= 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views },
                  { icon: 'ri-book-open-line', label: '总章节', value: `${comic.chapters}话` },
                  { icon: 'ri-star-fill', label: '评分', value: comic.rating },
                  { icon: 'ri-calendar-check-line', label: '更新日期', value: comic.updatedAt },
                ].map((stat) => (
                  <div key={stat.label} className="bg-background-100 rounded-xl p-4 text-center">
                    <div className="w-8 h-8 mx-auto mb-2 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className={`${stat.icon} text-primary-500 text-sm`}></i>
                    </div>
                    <p className="text-lg md:text-xl font-bold text-foreground-950">{stat.value}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-background-100 rounded-2xl p-5 md:p-6 sticky top-24">
                <div className="text-center mb-5">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-32 h-44 md:w-36 md:h-48 mx-auto rounded-xl object-cover object-top"
                  />
                  <h3 className="mt-3 font-semibold text-foreground-950">{comic.title}</h3>
                  <p className="text-xs text-foreground-500 mt-1">{comic.author}</p>
                </div>
                <Link
                  to={`/comic/${comic.slug}`}
                  className="block w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold text-center hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap mb-2"
                >
                  开始阅读
                </Link>
                <button className="w-full py-3 rounded-full border border-background-300 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-bookmark-line mr-1.5"></i>
                  加入收藏
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 mb-2">
              <i className="ri-double-quotes-l text-sm"></i>
              读者评论
            </span>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
              大家对这部作品的评价
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {reviews.slice(0, 3).map((review, idx) => (
              <div
                key={review.id}
                className={`rounded-2xl p-5 ${
                  idx === 0 ? 'bg-accent-100/60' : idx === 1 ? 'bg-secondary-100/60' : 'bg-primary-100/60'
                }`}
              >
                <div className="w-8 h-8 flex items-center justify-center mb-3">
                  <i className="ri-double-quotes-l text-xl text-primary-400"></i>
                </div>
                <p className="text-sm text-foreground-800 leading-relaxed mb-4">
                  "{review.content}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-foreground-200/30">
                  <img
                    src={review.avatar}
                    alt={review.userName}
                    className="w-9 h-9 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{review.userName}</p>
                    <p className="text-xs text-foreground-500">{review.role}</p>
                  </div>
                  <div className="ml-auto flex items-center gap-0.5">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-yellow-500 text-xs"></i>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {relatedComics.length > 0 && (
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                相关推荐
              </h2>
              <p className="text-sm text-foreground-500 mt-1">同类型热门作品</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 md:gap-5">
              {relatedComics.map((c) => (
                <Link
                  key={c.id}
                  to={`/comic/${c.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-2xl aspect-[3/4] mb-3">
                    <img
                      src={c.coverImage}
                      alt={c.title}
                      title={`哈哈漫画网 - ${c.title}`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {c.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{c.author}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-12 md:py-16 px-4 md:px-6 bg-background-50">
        <div className="max-w-xl mx-auto text-center">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">
            开始阅读《{comic.title}》
          </h2>
          <p className="text-sm text-foreground-500 mb-6">
            高清画质，流畅下拉式阅读体验，随时随地畅享精彩故事
          </p>
          <Link
            to={`/comic/${comic.slug}`}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            立即阅读
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </section>

      <Footer />
    </>
  );
}